import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, X, LayoutTemplate, Loader2, FileCheck, Share2, Upload } from "lucide-react";
import {
    DndContext,
    PointerSensor,
    useSensor,
    useSensors,
    closestCenter,
} from "@dnd-kit/core";
import {
    SortableContext,
    horizontalListSortingStrategy,
    useSortable,
    arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import TopNav from "../components/TopNav";
import TemplateEditor from "../components/TemplateEditor";
import SaveToWorkgroupDialog from "../components/SaveToWorkgroupDialog";
import {
    listTemplates,
    createTemplate,
    deleteTemplate,
    reorderTemplates,
} from "../lib/api";

export default function TemplatesPage() {
    const navigate = useNavigate();
    const [templates, setTemplates] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [importing, setImporting] = useState(false);
    const swiftFileInputRef = useRef(null);
    const [shareOpen, setShareOpen] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const data = await listTemplates();
            setTemplates(data);
            if (data.length > 0 && !activeId) {
                setActiveId(data[0].id);
            } else if (data.length === 0) {
                setActiveId(null);
            } else if (activeId && !data.find((t) => t.id === activeId)) {
                setActiveId(data[0]?.id || null);
            }
        } catch (e) {
            toast.error("Couldn't load templates");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleNewTemplate = async () => {
        setCreating(true);
        try {
            const name = `Template ${templates.length + 1}`;
            const created = await createTemplate({ name, description: "" });
            setTemplates((prev) => [...prev, created]);
            setActiveId(created.id);
            toast.success("New template created");
        } catch (e) {
            toast.error("Couldn't create template");
        } finally {
            setCreating(false);
        }
    };

    const handleImportSwift = async (file) => {
        if (!file) return;
        setImporting(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { importTemplateAny } = await import("../lib/api");
            const created = await importTemplateAny(fd);
            setTemplates((prev) => [...prev, created]);
            setActiveId(created.id);
            toast.success(`Imported: ${created.name}`);
            // refresh so categories + items appear in the tree
            load();
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.message || "Import failed";
            toast.error(`Couldn't import: ${msg}`);
        } finally {
            setImporting(false);
            if (swiftFileInputRef.current) swiftFileInputRef.current.value = "";
        }
    };

    const handleDeleteTab = async (e, id) => {
        e.stopPropagation();
        const tmpl = templates.find((t) => t.id === id);
        if (!tmpl) return;
        if (!window.confirm(`Delete "${tmpl.name}"? This removes all its categories and items.`)) {
            return;
        }
        try {
            await deleteTemplate(id);
            const next = templates.filter((t) => t.id !== id);
            setTemplates(next);
            if (activeId === id) setActiveId(next[0]?.id || null);
            toast.success("Template deleted");
        } catch (err) {
            toast.error("Couldn't delete template");
        }
    };

    const handleTemplateUpdated = (updated) => {
        setTemplates((prev) => prev.map((t) => (t.id === updated.id ? { ...t, ...updated } : t)));
    };

    // Drag-reorder tabs. Small distance constraint lets click-to-switch keep
    // working — only a real drag (> 5px) triggers reorder.
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const handleTabDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = templates.findIndex((t) => t.id === active.id);
        const newIndex = templates.findIndex((t) => t.id === over.id);
        if (oldIndex < 0 || newIndex < 0) return;
        const reordered = arrayMove(templates, oldIndex, newIndex);
        setTemplates(reordered); // optimistic
        reorderTemplates(reordered.map((t) => t.id)).catch(() => {
            toast.error("Couldn't save tab order");
            load(); // revert on failure
        });
    };

    const activeTemplate = templates.find((t) => t.id === activeId) || null;

    return (
        <div className="h-[100dvh] overflow-hidden blueprint-bg text-slate-100 flex flex-col" data-testid="templates-page">
            <TopNav active="templates" />

            {/* Page header */}
            <div className="shrink-0 border-b border-[#2A3655] bg-[#0A1128]/80">
                <div className="max-w-7xl mx-auto px-4 md:px-8 pt-6 pb-3 flex items-start justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500 mb-1">
                            <LayoutTemplate className="w-3.5 h-3.5 text-[#00A3FF]" />
                            Price Database
                        </div>
                        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-white">
                            Templates
                        </h1>
                        <p className="text-slate-400 text-sm mt-1 max-w-2xl">
                            Build a reusable pricing library for every service you sell. The AI
                            estimator will use these rates when generating quotes for your projects.
                        </p>
                    </div>
                    <div className="shrink-0 flex items-center gap-2">
                        {activeTemplate && !activeTemplate.workgroup_id && (
                            <button
                                onClick={() => setShareOpen(true)}
                                data-testid="btn-share-template"
                                title="Save to workgroup"
                                className="inline-flex items-center gap-2 px-3 md:px-4 py-2 bg-[#1C253C] hover:bg-[#00A3FF]/15 border border-[#2A3655] hover:border-[#00A3FF]/60 text-white text-xs md:text-sm font-semibold rounded-sm transition-colors"
                            >
                                <Share2 className="w-4 h-4 text-[#00A3FF]" />
                                <span className="hidden sm:inline">Save to workgroup</span>
                            </button>
                        )}
                        <button
                            onClick={() => navigate("/reports")}
                            data-testid="btn-templates-reports"
                            className="inline-flex items-center gap-2 px-3 md:px-4 py-2 bg-[#1C253C] hover:bg-[#2A3655] border border-[#2A3655] hover:border-[#00A3FF]/60 text-white text-xs md:text-sm font-semibold rounded-sm transition-colors"
                            aria-label="Open Reports"
                        >
                            <FileCheck className="w-4 h-4 text-[#00A3FF]" />
                            <span>Reports</span>
                        </button>
                    </div>
                </div>

                {/* Tab bar */}
                <div
                    className="max-w-7xl mx-auto px-2 md:px-6 flex items-end gap-1 overflow-x-auto scrollbar-thin"
                    data-testid="templates-tabbar"
                >
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleTabDragEnd}
                    >
                        <SortableContext
                            items={templates.map((t) => t.id)}
                            strategy={horizontalListSortingStrategy}
                        >
                            {templates.map((t) => (
                                <SortableTemplateTab
                                    key={t.id}
                                    template={t}
                                    active={t.id === activeId}
                                    onPick={() => setActiveId(t.id)}
                                    onDelete={(e) => handleDeleteTab(e, t.id)}
                                />
                            ))}
                        </SortableContext>
                    </DndContext>
                    <button
                        onClick={handleNewTemplate}
                        disabled={creating}
                        data-testid="btn-new-template"
                        className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wider text-[#00A3FF] hover:bg-[#1C253C] rounded-t-sm transition-colors shrink-0 disabled:opacity-50"
                    >
                        {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                        New template
                    </button>
                    <input
                        ref={swiftFileInputRef}
                        type="file"
                        accept=".SwiftTemplates,application/zip,application/x-zip-compressed,image/png,image/jpeg,image/webp,image/heic,image/heif"
                        onChange={(e) => handleImportSwift(e.target.files?.[0])}
                        className="hidden"
                        data-testid="input-import-swift"
                    />
                    <button
                        onClick={() => swiftFileInputRef.current?.click()}
                        disabled={importing}
                        data-testid="btn-import-swift"
                        title="Import a Planswift .SwiftTemplates file or any screenshot / photo of a pricing sheet (AI will extract it)"
                        className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wider text-slate-300 hover:text-white hover:bg-[#1C253C] rounded-t-sm transition-colors shrink-0 disabled:opacity-50"
                    >
                        {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 text-[#00A3FF]" />}
                        Import
                    </button>
                </div>
            </div>

            {/* Body */}
            <main className="flex-1 min-h-0 overflow-y-auto" data-testid="templates-body">
                {loading ? (
                    <div className="flex items-center justify-center py-20 text-slate-400 text-sm">
                        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading templates…
                    </div>
                ) : !activeTemplate ? (
                    <EmptyState onCreate={handleNewTemplate} creating={creating} />
                ) : (
                    <TemplateEditor
                        key={activeTemplate.id}
                        template={activeTemplate}
                        onTemplateUpdated={handleTemplateUpdated}
                    />
                )}
            </main>
            <SaveToWorkgroupDialog
                kind="template"
                itemId={activeTemplate?.id}
                itemName={activeTemplate?.name}
                settingKey="share_templates"
                open={shareOpen}
                onOpenChange={setShareOpen}
                onSaved={() => load()}
            />
        </div>
    );
}

function SortableTemplateTab({ template, active, onPick, onDelete }) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: template.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        zIndex: isDragging ? 10 : "auto",
    };

    return (
        <button
            ref={setNodeRef}
            style={style}
            onClick={onPick}
            data-testid={`template-tab-${template.id}`}
            {...attributes}
            {...listeners}
            className={`group relative flex items-center gap-2 pl-4 pr-2 py-2 text-sm font-medium rounded-t-sm border-x border-t border-[#2A3655] transition-colors shrink-0 cursor-grab active:cursor-grabbing ${
                active
                    ? "bg-[#0F1B3A] text-white border-b-transparent -mb-px border-t-2 border-t-[#00A3FF]"
                    : "bg-[#0A1128] text-slate-400 hover:text-white hover:bg-[#131A2F]"
            }`}
        >
            <span className="max-w-[180px] truncate pointer-events-none">
                {template.name}
            </span>
            <span
                onClick={onDelete}
                onPointerDown={(e) => e.stopPropagation()}
                className="w-5 h-5 inline-flex items-center justify-center rounded-sm hover:bg-red-500/30 hover:text-red-300 opacity-50 group-hover:opacity-100 transition-all cursor-pointer"
                data-testid={`template-tab-close-${template.id}`}
                role="button"
                aria-label={`Delete ${template.name}`}
            >
                <X className="w-3 h-3" />
            </span>
        </button>
    );
}

function EmptyState({ onCreate, creating }) {
    return (
        <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="templates-empty-state">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#00A3FF]/10 border border-[#00A3FF]/30 mb-4">
                <LayoutTemplate className="w-7 h-7 text-[#00A3FF]" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">No templates yet</h2>
            <p className="text-slate-400 text-sm mb-6">
                Create your first template to start building your company's pricing database.
                Group services into categories (e.g. <span className="text-slate-200">Excavation</span>,
                {" "}<span className="text-slate-200">Concrete</span>) and add line items with fixed,
                per-item, or per-length pricing.
            </p>
            <button
                onClick={onCreate}
                disabled={creating}
                data-testid="btn-empty-new-template"
                className="btn-glow inline-flex items-center gap-2 bg-[#007AFF] hover:bg-[#3395FF] disabled:opacity-60 text-white font-bold uppercase tracking-wider px-6 py-3 rounded-md text-sm"
            >
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Create your first template
            </button>
        </div>
    );
}
